import {
  PLANNING_HOUR_END,
  PLANNING_HOUR_START,
  type StorePlanningClockStatus,
  type StorePlanningCoverageKind,
  storeProcessTaskTheme,
} from '@gozoki/api-types'

// Géométrie de la grille, en px du canvas logique 1920 × 1080 (cotes Figma 10715:56059 lues 1:1).
// La maquette dessine une heure sur 48 px, soit 776 px de grille : c'est plus que la hauteur que
// la colonne centrale du dashboard peut offrir une fois le widget Flux supply posé au-dessus. On
// garde ces cotes réelles et le widget fait défiler verticalement (cf. `PlanningWeekCard`).
export const ROW_HEIGHT = 48
export const GRID_TOP = 8
export const ROW_COUNT = PLANNING_HOUR_END - PLANNING_HOUR_START
export const GRID_HEIGHT = GRID_TOP + ROW_COUNT * ROW_HEIGHT

// Ligne de titres d'un jour (pastille du jour) + son écart de 8 px avec la grille : hauteur du
// bandeau d'en-tête épinglé pendant le défilement.
export const DAY_HEADER_HEIGHT = 33

// Colonnes internes d'un jour : affluence | couverture | timeline, séparées de 4 px.
export const AFFLUENCE_WIDTH = 18
export const COVERAGE_WIDTH = 6
export const COLUMN_GAP = 4

// Carte collaborateur : l'avatar et le badge de pointage occupent une hauteur fixe au-dessus de
// l'heure de début, et le libellé de fin une hauteur fixe en dessous de l'heure de fin.
export const SHIFT_CARD_WIDTH = 40
export const SHIFT_CARD_HEAD = 64
export const SHIFT_CARD_FOOT = 16
export const SHIFT_CARD_LANE_OFFSET = 34

export const DAY_LABELS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'] as const

// Dégradé « Verts Lucie » repris du widget « Couverture planning » du dashboard RH
// (back-office colors.ts GREEN_RAMP) : blanc pur → vert foncé, interpolé en continu.
const GREEN_RAMP = ['#FFFFFF', '#CEE3C4', '#A2C396', '#6A9560', '#4F6A47'] as const

// Au-delà de cette densité normalisée (0..1), le fond est trop sombre pour du texte foncé.
const DENSITY_LIGHT_TEXT_FROM = 0.7

const hexToRgb = (hex: string): [number, number, number] => {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

// Teinte verte CONTINUE le long de GREEN_RAMP pour une densité normalisée 0..1 : interpolation entre
// paliers (pas de sauts), identique au `rampColor` du dashboard RH.
const rampColor = (norm: number): string => {
  const scaled = Math.min(1, Math.max(0, norm)) * (GREEN_RAMP.length - 1)
  const lo = Math.floor(scaled)
  const hi = Math.min(GREEN_RAMP.length - 1, lo + 1)
  const t = scaled - lo
  const [r1, g1, b1] = hexToRgb(GREEN_RAMP[lo])
  const [r2, g2, b2] = hexToRgb(GREEN_RAMP[hi])
  return `rgb(${Math.round(lerp(r1, r2, t))} ${Math.round(lerp(g1, g2, t))} ${Math.round(lerp(b1, b2, t))})`
}

export const affluenceCellStyle = (value: number, max: number) => {
  const norm = value <= 0 || max <= 0 ? 0 : value / max
  return {
    backgroundColor: rampColor(norm),
    color: norm >= DENSITY_LIGHT_TEXT_FROM ? '#F9F8F1' : '#152013',
  }
}

export const COVERAGE_COLOR: Record<StorePlanningCoverageKind, string> = {
  presentiel: '#79627B',
  distanciel: '#A38FA5',
  ouverture: '#C7B4C8',
}

export const COVERAGE_LABEL: Record<StorePlanningCoverageKind, string> = {
  presentiel: 'Présentiel',
  distanciel: 'Distanciel',
  ouverture: 'Ouvertures',
}

// Bloc « tâche process » dans la grille : palette partagée avec le back-office
// (`storeProcessTaskTheme`, source de vérité dans @gozoki/api-types). `bar` = liseré gauche,
// `bg` = fond du bloc, `text` = icône + heure.
export const TASK_THEME = storeProcessTaskTheme

export const CLOCK_COLOR: Record<StorePlanningClockStatus, string> = {
  on_time: '#2A9D5C',
  late: '#F0A945',
  missing: '#F04545',
  // Neutre : un créneau à venir n'est ni conforme ni en anomalie.
  upcoming: '#8A8676',
}

export const CLOCK_LABEL: Record<StorePlanningClockStatus, string> = {
  on_time: 'Pointé',
  late: 'Pointé en retard — à valider',
  missing: 'Non pointé',
  upcoming: 'À venir',
}

export const minutesOf = (time: string): number => {
  const [hours, minutes] = time.split(':')
  return Number(hours) * 60 + Number(minutes ?? 0)
}

// Ordonnée d'un instant dans la grille, bornée à la plage affichée.
export const yOf = (minutes: number, rowHeight: number = ROW_HEIGHT): number => {
  const offset = (minutes - PLANNING_HOUR_START * 60) / 60
  return GRID_TOP + Math.max(0, Math.min(ROW_COUNT, offset)) * rowHeight
}

export const HOUR_LABELS = Array.from(
  { length: ROW_COUNT + 1 },
  (_, index) => `${String(PLANNING_HOUR_START + index).padStart(2, '0')}:00`,
)
